import type { AttemptView, PaletteState, PlayerQuestion } from '../../../lib/api-types';

/** What the learner has picked in this session, on top of what the server last sent. */
export interface Local {
  choice: number | null;
  marked: boolean;
}

export type OpenQuestion = PlayerQuestion & { n: number; section: string };

export const stateOf = (l: Local): PaletteState =>
  l.choice !== null
    ? l.marked
      ? 'answered_marked'
      : 'answered'
    : l.marked
      ? 'marked'
      : 'not_answered';

/** The palette state for one question: the local pick wins over the server's last word. */
export const paletteState = (
  local: Readonly<Record<string, Local>>,
  p: { itemId: string; state: PaletteState },
): PaletteState => {
  const l = local[p.itemId];
  return l ? stateOf(l) : p.state;
};

/** Questions of the sections that are open now, in order, with their palette number. */
export function openQuestions(view: AttemptView | null): OpenQuestion[] {
  if (!view) return [];
  return view.sections
    .filter((s) => s.state === 'open')
    .flatMap((s) =>
      s.questions.map((q) => ({
        ...q,
        section: s.subjectName,
        n: s.palette.find((p) => p.itemId === q.itemId)?.n ?? 0,
      })),
    );
}

/** Counts for the submit confirmation (every section, not only the open ones). */
export function answerCounts(view: AttemptView, local: Readonly<Record<string, Local>>) {
  const counts = { answered: 0, marked: 0, left: 0 };
  for (const s of view.sections)
    for (const p of s.palette) {
      const st = paletteState(local, p);
      if (st === 'answered' || st === 'answered_marked') counts.answered++;
      else counts.left++;
      if (st === 'marked' || st === 'answered_marked') counts.marked++;
    }
  return counts;
}

export const isLastUnit = (view: Pick<AttemptView, 'unitIdx' | 'paper'>): boolean =>
  view.unitIdx >= view.paper.units.length - 1;

export const isManaged = (view: Pick<AttemptView, 'managedBy'> | null): boolean =>
  view?.managedBy === 'grand_mock';
